import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { BookOpen, Plus, Loader2, Search, Pencil, Trash2, FileText, HelpCircle, Wrench, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { id: 'troubleshooting', label: 'Troubleshooting', icon: Wrench, color: 'bg-red-100 text-red-700' },
  { id: 'how_to', label: 'How-To', icon: BookOpen, color: 'bg-blue-100 text-blue-700' },
  { id: 'setup', label: 'Setup Guide', icon: Settings, color: 'bg-emerald-100 text-emerald-700' },
  { id: 'faq', label: 'FAQ', icon: HelpCircle, color: 'bg-amber-100 text-amber-700' },
];

export default function KnowledgeBase({ token }) {
  const [articles, setArticles] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ platform_type_id: '', title: '', content: '', category: 'troubleshooting', tags: [] });
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/knowledge-base`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
      ]);
      setArticles(a.data.articles || []);
      setPlatforms(p.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    let result = articles;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(a => a.title.toLowerCase().includes(q) || a.platform_name?.toLowerCase().includes(q));
    }
    if (filterCat) result = result.filter(a => a.category === filterCat);
    return result;
  }, [articles, search, filterCat]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ platform_type_id: '', title: '', content: '', category: 'troubleshooting', tags: [] });
    setEditOpen(true);
  };

  const openEdit = (article) => {
    setEditingId(article.id);
    setForm({ platform_type_id: article.platform_type_id, title: article.title, content: article.content, category: article.category, tags: article.tags || [] });
    setEditOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/admin/knowledge-base/${editingId}`, form, { headers });
        toast.success('Article updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/knowledge-base`, form, { headers });
        toast.success('Article created');
      }
      setEditOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/knowledge-base/${id}`, { headers });
      toast.success('Deleted');
      if (selected?.id === id) setSelected(null);
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="flex h-full" data-testid="knowledge-base">
      <div className="w-72 border-r bg-muted/20 flex flex-col shrink-0">
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Knowledge Base</h3>
            <Button variant="outline" size="sm" onClick={openCreate} className="h-7 gap-1 text-xs" data-testid="create-article-btn"><Plus className="h-3 w-3" /> New</Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
          </div>
          <div className="flex flex-wrap gap-1">
            <button onClick={() => setFilterCat('')} className={`px-2 py-0.5 rounded text-[10px] font-medium ${!filterCat ? 'bg-primary text-primary-foreground' : 'bg-muted/60 text-muted-foreground hover:text-foreground'}`}>All</button>
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setFilterCat(c.id)} className={`px-2 py-0.5 rounded text-[10px] font-medium ${filterCat === c.id ? 'bg-primary text-primary-foreground' : 'bg-muted/60 text-muted-foreground hover:text-foreground'}`}>{c.label}</button>
            ))}
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {filtered.map(a => {
              const cat = CATEGORIES.find(c => c.id === a.category);
              return (
                <button key={a.id} onClick={() => setSelected(a)} data-testid={`article-${a.id}`}
                  className={`w-full text-left p-3 rounded-lg transition-colors text-sm ${selected?.id === a.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/60'}`}>
                  <div className="font-medium truncate">{a.title}</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {cat && <Badge className={`text-[8px] ${cat.color}`}>{cat.label}</Badge>}
                    <span className="text-[10px] text-muted-foreground">{a.platform_name}</span>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No articles found</p>}
          </div>
        </ScrollArea>
      </div>

      <div className="flex-1 overflow-auto">
        {selected ? (
          <div className="p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{selected.title}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline">{selected.platform_name}</Badge>
                  {CATEGORIES.find(c => c.id === selected.category) && <Badge className={CATEGORIES.find(c => c.id === selected.category).color + ' text-[10px]'}>{CATEGORIES.find(c => c.id === selected.category).label}</Badge>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(selected)} className="gap-1 text-xs"><Pencil className="h-3 w-3" /> Edit</Button>
                <Button variant="outline" size="sm" onClick={() => deleteArticle(selected.id)} className="gap-1 text-xs text-destructive"><Trash2 className="h-3 w-3" /> Delete</Button>
              </div>
            </div>
            <Separator className="mb-4" />
            <div className="prose prose-sm max-w-none text-sm leading-relaxed whitespace-pre-wrap">{selected.content}</div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center"><BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">Select an article or create a new one</p><p className="text-xs mt-1">{articles.length} articles in knowledge base</p></div>
          </div>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Article' : 'New Article'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 flex-1 overflow-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium">Platform</label>
                <Select value={form.platform_type_id} onValueChange={v => setForm(p => ({ ...p, platform_type_id: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-sm font-medium">Category</label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><label className="text-sm font-medium">Title</label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="PMS won't check in guest — troubleshooting steps" className="mt-1" data-testid="article-title" /></div>
            <div><label className="text-sm font-medium">Content</label><Textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="Step 1: Verify the reservation exists..." className="mt-1 min-h-[200px] text-sm" rows={10} data-testid="article-content" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.platform_type_id || !form.content}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{editingId ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
